// src/components/Dealer/index.js
import React, { useState, useEffect } from 'react';
import EditDealerModal from './EditDealerModal';
import AddDealerForm from './AddDealerForm';
import { Table, Button, Input, Popconfirm } from 'antd';
import { EditOutlined, DeleteOutlined, SearchOutlined, PlusOutlined } from '@ant-design/icons';
import { getDealers, createDealer, updateDealer, deleteDealer } from '../../services/api';
import toast from 'react-hot-toast';

const DealerManagement = ({ setTitle }) => {
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingDealer, setEditingDealer] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddDealerModalVisible, setIsAddDealerModalVisible] = useState(false);
  const [editDealerLoading, setEditDealerLoading] = useState(false);
  const [addDealerLoading, setAddDealerLoading] = useState(false);

  useEffect(() => {
    setTitle('EarthVerse CRM - 經銷管理');
    fetchDealers();
  }, [setTitle]);

  const fetchDealers = async () => {
    try {
      setLoading(true);
      const fetchedDealers = await getDealers();
      setDealers(fetchedDealers);
    } catch (error) {
      console.error('Error fetching dealers:', error);
      toast.error('Failed to fetch dealers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDealer = async (dealerData) => {
    try {
      setAddDealerLoading(true);
      const newDealer = await createDealer(dealerData);
      setDealers(prevDealers => [...prevDealers, newDealer]);
      toast.success('Dealer added successfully!');
      setIsAddDealerModalVisible(false);
    } catch (error) {
      console.error('Error creating dealer:', error);
      toast.error('Failed to add dealer. Please try again.');
    } finally {
      setAddDealerLoading(false);
    }
  };

  const handleUpdateDealer = async (dealerId, dealerData) => {
    try {
      setEditDealerLoading(true);
      const updatedDealer = await updateDealer(dealerId, dealerData);
      setDealers(prevDealers => prevDealers.map(dealer =>
        dealer.id === dealerId ? { ...dealer, ...updatedDealer } : dealer
      ));
      toast.success('Dealer updated successfully!');
      setIsEditModalVisible(false);
      setEditingDealer(null);
    } catch (error) {
      console.error('Error updating dealer:', error);
      toast.error('Failed to update dealer. Please try again.');
    } finally {
      setEditDealerLoading(false);
    }
  };

  const handleDeleteDealer = async (dealerId) => {
    try {
      await deleteDealer(dealerId);
      setDealers(prevDealers => prevDealers.filter(dealer => dealer.id !== dealerId));
      toast.success('Dealer deleted successfully!');
    } catch (error) {
      console.error('Error deleting dealer:', error);
      toast.error('Failed to delete dealer. Please try again.');
    }
  };

  const handleEdit = (dealer) => {
    setEditingDealer(dealer);
    setIsEditModalVisible(true);
  };

  const handleEditCancel = () => {
    setIsEditModalVisible(false);
    setEditingDealer(null);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Last Updated',
      dataIndex: 'last_updated',
      key: 'last_updated',
      render: (date) => new Date(date).toLocaleString('zh-TW'),
      sorter: (a, b) => new Date(a.last_updated) - new Date(b.last_updated),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <span>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} style={{ marginRight: 8 }} />
          <Popconfirm
            title="Are you sure you want to delete this dealer?"
            onConfirm={() => handleDeleteDealer(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </span>
      ),
    },
  ];

  const filteredDealers = dealers.filter(dealer =>
    dealer.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="dealer-list-container">
      <h2>經銷商管理</h2>
      <div className="dealer-actions" style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Input
          placeholder="Search dealers..."
          prefix={<SearchOutlined />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ width: 200, marginBottom: 16 }}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setIsAddDealerModalVisible(true)}
          style={{ marginBottom: 16 , marginLeft: 16}}
        >
          Add New Dealer
        </Button>
      </div>
      <Table
        dataSource={filteredDealers}
        columns={columns}
        rowKey="id"
        pagination={{ pageSize: 30 }}
        loading={loading}
      />
      <AddDealerForm
        visible={isAddDealerModalVisible}
        onCancel={() => setIsAddDealerModalVisible(false)}
        onAddDealer={handleAddDealer}
        loading={addDealerLoading}
      />
      <EditDealerModal
        visible={isEditModalVisible}
        onCancel={handleEditCancel}
        onSave={handleUpdateDealer}
        dealer={editingDealer || {}}
        loading={editDealerLoading}
      />
    </div>
  );
};

export default DealerManagement;